import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import { GRAPHQL_HTTP_URL } from '../config/api';
import { ADMIN_LOGIN_RETURNING_KEY } from '../constants/adminLoginStorage';

function messageForGatewayTimeout(err, fallback) {
  const status = err?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to respond. Please try again.';
  }
  return fallback;
}

export default function Login({ accessDeniedMessage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const resetMessage = location.state?.resetMessage;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(accessDeniedMessage || '');
  const [submitting, setSubmitting] = useState(false);
  const [isReturningUser] = useState(
    () => localStorage.getItem(ADMIN_LOGIN_RETURNING_KEY) === '1',
  );

  useEffect(() => {
    if (accessDeniedMessage) setError(accessDeniedMessage);
  }, [accessDeniedMessage]);

  const requestCodeAfterPassword = async () => {
    setError('');

    const query = `
      mutation RequestAdminPasswordLoginOtp($input: SignUpUserInput!) {
        requestAdminPasswordLoginOtp(input: $input) {
          otpSessionId
          expiresInSeconds
          resendCooldownSeconds
          codeSentToEmail
        }
      }
    `;

    setSubmitting(true);
    try {
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: {
              email: email.trim(),
              password,
            },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      const gqlErrors = response.data?.errors;
      const data = response.data?.data?.requestAdminPasswordLoginOtp;

      if (data?.otpSessionId) {
        navigate('/login/verify-code', {
          replace: true,
          state: {
            otpSessionId: data.otpSessionId,
            codeSentToEmail: data.codeSentToEmail,
            email: email.trim(),
            password,
          },
        });
      } else {
        const backendMsg = gqlErrors?.[0]?.message;
        setError(backendMsg || 'Invalid credentials or could not send code.');
      }
    } catch (err) {
      setError(
        messageForGatewayTimeout(
          err,
          'Login failed. Please check your credentials or try again later.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await requestCodeAfterPassword();
  };

  return (
    <div className="ld-root">
      <div className="ld-grid">

        {/* ── Left: hero panel ── */}
        <div className="ld-left">
          <div className="ld-hero">
            <img
              src="/getstarted2.png"
              alt="SAS Cleaning Suite"
              className="ld-hero-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement.querySelector('.ld-hero-logo').style.display = 'block';
              }}
            />
            <img
              src="/logoSas.png?v=5"
              alt="SAS Logo"
              className="ld-hero-logo"
              style={{ display: 'none' }}
            />
          </div>
          <p className="ld-tagline">Trusted by cleaning businesses</p>
          <p className="ld-tagline-sub">
            Manage your operations, schedule, and team all in one place.
          </p>
        </div>

        {/* ── Right: form ── */}
        <div className="ld-right">
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 className="ld-heading">
              {isReturningUser ? 'Welcome back' : 'Sign in to your account'}
            </h2>
            <p className="ld-subheading">
              Sign in with your password, then enter the code sent to your email
            </p>
          </div>

          <form className="ld-form" onSubmit={handleSubmit} noValidate>
            {resetMessage && <div className="ld-info">{resetMessage}</div>}
            {error && <div className="ld-error">{error}</div>}

            <div className="ld-field">
              <label className="ld-label">Email address</label>
              <input
                className="ld-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                disabled={submitting}
              />
            </div>

            <div className="ld-field">
              <div className="ld-field-row">
                <label className="ld-label">Password</label>
                <Link to="/forgot-password" className="ld-forgot">
                  Forgot password?
                </Link>
              </div>
              <div className="ld-pw-wrap">
                <input
                  className="ld-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="ld-pw-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <button type="submit" className="ld-submit" disabled={submitting}>
              {submitting ? 'Sending code...' : 'Continue'}
            </button>
          </form>

          <div className="ld-footer">
            <p>
              By continuing, you agree to our{' '}
              <Link to="/terms-conditions" className="ld-footer-link">Terms &amp; Conditions</Link>
              {' '}and{' '}
              <Link to="/data-usage-policy" className="ld-footer-link">Privacy Policy</Link>
            </p>
            <p className="ld-signup-link">
              Don&apos;t have an account?{' '}
              <span onClick={() => window.open('https://sascleaningsuite.com', '_blank')}>
                Sign up
              </span>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
